import * as React from 'react';

import FormikFormattedTextField from '../../../components/FormikInputWrappers/FormikFormattedTextField';
import FormikSelectInput from '../../../components/FormikInputWrappers/FormikSelectInput';
import FormikTextField from '../../../components/FormikInputWrappers/FormikTextField';
import { NegotiationOption, NegotiationOptionExtraConfirmationInfoType, OutDebt } from '../../../generated/graphql';
import { GENERIC_QUESTIONS, IncomeFormField } from './incomeFormConfig';

interface IncomeOriginBasicFormProps {
  formik: any;
  confInfoType?: NegotiationOptionExtraConfirmationInfoType;
  outDebt: OutDebt,
  negOption: NegotiationOption;
}

export type IncomeFormRef = {
  initialValue: Record<string, string | number | boolean>,
  validation: {
    rules: Record<string, string>,
    messages: Record<string, string>
  },
};

const IncomeOriginBasicForm = React.forwardRef<IncomeFormRef, IncomeOriginBasicFormProps>(
  (props, ref) => {
    const { formik } = props;
    const config = GENERIC_QUESTIONS;

    const {
      questions,
      initialValue = {},
      validation = { rules: {}, messages: {} },
    } = React.useMemo(() => {
      return config.reduce((
        acc, {
          validation: { rules, messages = {} } = {},
          initialValue: innerInitialValue = '',
          ...question
        },
      ) => {
        return {
          questions: [ ...acc.questions, question ],
          initialValue: { ...acc.initialValue, [question.id]: innerInitialValue },
          validation: rules ? {
            rules: { ...acc.validation.rules, [question.id]: rules },
            messages: { ...acc.validation.messages, ...messages },
          } : acc.validation,
        };
      }, {
        initialValue: {},
        questions: [],
        validation: { rules: {}, messages: {} },
      } as IncomeFormRef & { questions: IncomeFormField[] });
    }, [ config ]);

    React.useImperativeHandle(ref, () => ({
      initialValue,
      validation,
    }));

    return (
      <>
        {questions.map(({ id, visible, ...question }) => {
          if (typeof visible === 'function' && !visible(formik.values)) return null;

          // we cannot destructure the type so the transpiler can identify
          // which form field type is
          switch (question.type) {
            case 'select':
              return (
                <FormikSelectInput
                  fullWidth
                  displayEmpty
                  className="mb-2"
                  formik={formik}
                  valueId={id}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...question}
                />
              );

            case 'numeric':
              return (
                <FormikFormattedTextField
                  fullWidth
                  className="mb-2"
                  formik={formik}
                  valueId={id}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...question}
                />
              );

            case 'text':
              return (
                <FormikTextField
                  fullWidth
                  className="mb-2"
                  formik={formik}
                  valueId={id}
                  // eslint-disable-next-line react/jsx-props-no-spreading
                  {...question}
                />
              );

            default:
              return null;
          }
        })}
      </>
    );
  },
);

export default IncomeOriginBasicForm;
